import React from 'react';
import { MousePointer2Icon } from 'lucide-react';
import { pencilCaptions, pencilComposerText } from '../../data/pencilCanvas';
import { SceneProps } from '../../types/reel';
import { clamp, easeInOut, easeOut, lerp, progress, punch, reveal } from '../../utils/reelMath';
import { BottomDock, TopLeftBar, TopRightBar, ZoomControl } from './pencil/PencilChrome';
import { PencilAssetCard } from './pencil/PencilAssetCard';
import { CommentPin, CommentSidebar, Composer } from './pencil/PencilComments';
import { SceneLayer } from './SceneLayer';

/** Pencil's stage is 1440×900; the window renders it at this scale inside the reel. */
const STAGE_W = 1440;
const STAGE_H = 900;
const WINDOW_SCALE = 0.875;

/** Camera that centers world point (wx, wy) at the given zoom. */
function focus(wx: number, wy: number, scale: number) {
  return {
    x: STAGE_W / 2 - wx * scale,
    y: STAGE_H / 2 - wy * scale,
    scale
  };
}
const ZOOMED = focus(950, 560, 1.55);

/**
 * Slides 11 → 12 of "Infinite, Explained." as one shot:
 * pick the comment tool → panel slides in → cursor becomes a pin → click →
 * camera flies in → composer types → send flips to Pencil yellow.
 */
export function PencilScene({
  time,
  scene
}: SceneProps) {
  const s = scene.start;
  const toolClick = s + 0.45;
  const pinAt = s + 0.95;
  const zoomStart = s + 1.1;
  const typeStart = s + 1.45;
  const cps = 38;
  const tool = time >= toolClick ? 'comment' : 'select';
  const sidebarIn = easeOut(progress(time, toolClick, toolClick + 0.3));

  // Cursor: drifts to the dock's comment tool, then out to where the pin lands.
  const toDock = easeInOut(progress(time, s, toolClick));
  const toPin = easeInOut(progress(time, toolClick + 0.1, pinAt));
  const cursorX = lerp(lerp(900, 674, toDock), 850, toPin);
  const cursorY = lerp(lerp(620, 864, toDock), 490, toPin);
  const cursorOpacity = time < pinAt ? 1 : 0;
  const clickPulse = (at: number) => time >= at && time < at + 0.25 ? 1 - progress(time, at, at + 0.25) : 0;
  const zoom = easeInOut(progress(time, zoomStart, zoomStart + 0.8));
  const cam = {
    x: lerp(0, ZOOMED.x, zoom),
    y: lerp(0, ZOOMED.y, zoom),
    scale: lerp(1, ZOOMED.scale, zoom)
  };
  const chars = Array.from(pencilComposerText);
  const typed = Math.floor(clamp((time - typeStart) * cps, 0, chars.length));
  const composerIn = reveal(time, zoomStart + 0.1, 0.25);
  const caretOn = time < typeStart + chars.length / cps || Math.floor(time * 2.4) % 2 === 0;
  const captionB = easeOut(progress(time, zoomStart, zoomStart + 0.3));
  return <SceneLayer time={time} start={scene.start} end={scene.end} fade={0.12} data-id="element-270" data-mp="2butdbaq35nvh">
      {({
      p
    }) => <div className="absolute inset-0 bg-cream" data-id="element-271" data-mp="27208ynyj4hjx">
          {/* Captions — slide 11 title morphs into slide 12 */}
          <div className="absolute left-[150px] top-[330px] w-[360px]" data-id="element-272" data-mp="222dvcdesgzal">
            <p className="font-mono text-[18px] text-muted" data-id="element-273" data-mp="2zknnfkj0syc1">Pencil · AI B2B canvas</p>
            <div className="relative mt-5 h-[300px]" data-id="element-274" data-mp="2us492lcidz66">
              {pencilCaptions.map((c, i) => {
            const o = i === 0 ? 1 - captionB : captionB;
            return <div key={c.title} className="absolute inset-0" style={{
              opacity: o,
              transform: `translateY(${(i === 0 ? -captionB : 1 - captionB) * 16}px)`,
              filter: `blur(${(1 - o) * 4}px)`
            }} data-id="element-275" data-mp="372lk2og3sexu" data-mpi={i}>
                    <h2 className="text-[60px] font-medium leading-[1.04] tracking-[-0.045em] text-ink" data-id="element-276" data-mp="3l3frb3rf09ux">{c.title}</h2>
                    <p className="mt-6 text-[22px] text-muted" data-id="element-277" data-mp="3q1hcbu8eij91">{c.body}</p>
                  </div>;
          })}
            </div>
          </div>

          {/* Pencil window */}
          <div className="absolute left-[560px] top-[146px] h-[787.5px] w-[1260px]" style={{
        transform: `scale(${punch(time, s, 0.04, 0.4) + 0.015 * p})`,
        transformOrigin: '60% 50%'
      }} data-id="element-278" data-mp="3mxx5vblr01i">
            <div className="absolute left-0 top-0 h-[900px] w-[1440px] origin-top-left overflow-hidden rounded-[30px] border-[5px] border-ink bg-[#F5F5F5] font-ui text-[#191915] shadow-[16px_16px_0_#121314]" style={{
          transform: `scale(${WINDOW_SCALE})`
        }} data-id="element-279" data-mp="co4gsd9jsqyz">
              {/* World */}
              <div className="absolute left-0 top-0 h-px w-px origin-top-left" style={{
            transform: `translate(${cam.x}px, ${cam.y}px) scale(${cam.scale})`
          }} data-id="element-280" data-mp="8sjlbpcfa22q">
                <PencilAssetCard x={487} y={263} data-id="element-281" data-mp="3c98kurdl8biw" />
                {time >= pinAt && composerIn < 1 && <CommentPin x={830} y={470} style={{
              opacity: 1 - composerIn
            }} data-id="element-282" data-mp="387dh7g86pm1y" />}
                {time >= zoomStart && <Composer x={797} y={513} text={chars.slice(0, typed).join('')} caretOn={caretOn} style={{
              opacity: composerIn,
              transform: `scale(${0.96 + 0.04 * composerIn})`
            }} data-id="element-283" data-mp="3h68o6uj1z06b" />}
              </div>

              <TopLeftBar data-id="element-284" data-mp="2eclbr7poj3jw" />
              <TopRightBar data-id="element-285" data-mp="2ecbklgsweqcm" />
              <CommentSidebar style={{
            opacity: sidebarIn,
            transform: `translateX(${(1 - sidebarIn) * 40}px)`
          }} data-id="element-286" data-mp="2jcpul4e28ada" />
              <BottomDock tool={tool} data-id="element-287" data-mp="25d81ictqeay4" />
              <ZoomControl scale={cam.scale} data-id="element-288" data-mp="25e9mb90jubt1" />

              {/* Cursor + click ripple (screen space) */}
              <div className="absolute left-0 top-0 z-30" style={{
            transform: `translate(${cursorX}px, ${cursorY}px)`,
            opacity: cursorOpacity
          }} data-id="element-289" data-mp="2ackwxlyo4qcb">
                <MousePointer2Icon size={26} className="fill-ink text-white" strokeWidth={1.5} data-id="element-290" data-mp="322z5r3rtrch4" />
              </div>
              <span className="absolute z-30 h-12 w-12 rounded-full border-2 border-ink" style={{
            left: 650,
            top: 840,
            opacity: clickPulse(toolClick),
            transform: `scale(${1.4 - 0.4 * clickPulse(toolClick)})`
          }} data-id="element-291" data-mp="2oclzd5ul05tw" />
              <span className="absolute z-30 h-12 w-12 rounded-full border-2 border-ink" style={{
            left: 826,
            top: 466,
            opacity: clickPulse(zoomStart) * (1 - zoom),
            transform: `scale(${1.4 - 0.4 * clickPulse(zoomStart)})`
          }} data-id="element-292" data-mp="3gyn6uk1ujjgs" />
            </div>
          </div>
        </div>}
    </SceneLayer>;
}